
import * as React from 'react';

import {
    Button,
    Dialog,
    DropdownMenu,
    Heading,
    Icon,
    Input,
    Label,
    Popover,
    TextArea,
    Text,
    Tooltip
} from '@/components/ui';

import {
    createFamily,
    PreviewStack,
    VariantPreview,
    withStorySources
} from './helpers';

const DialogHero = () => {
    const [open, setOpen] = React.useState(false);
    const close = () => setOpen(false);

    return (
        <>
            <Button appearance="outline" tone="neutral" onClick={() => setOpen(true)}>Invite teammates</Button>
            <Dialog
                open={open}
                onClose={close}
                title="Invite teammates"
                description="Use Dialog for workflows that need context, form fields, or multiple editable inputs."
                actions={[
                    { label: 'Cancel', onClick: close, appearance: 'outline', tone: 'neutral' },
                    { label: 'Send invites', onClick: () => {
                        alert('Invite sent');
                        close();
                    } }
                ]}
            >
                <div className="tw-grid tw-gap-5">
                    <div className="tw-grid tw-gap-2">
                        <Label htmlFor="dialog-emails">Emails</Label>
                        <Input
                            id="dialog-emails"
                            placeholder="alexandre@wisemetering.com, team@client.com"
                        />
                    </div>
                    <div className="tw-grid tw-gap-2">
                        <Label htmlFor="dialog-message">Message</Label>
                        <TextArea
                            id="dialog-message"
                            placeholder="Add a short note for the invitation email."
                            rows={4}
                        />
                    </div>
                </div>
            </Dialog>
        </>
    );
};

const DropdownMenuHero = () => (
    <DropdownMenu
        align="start"
        sideOffset={12}
        items={[
            { type: 'header', label: 'Actions' },
            { label: 'Edit', icon: 'edit', onSelect: () => alert('Edit') },
            { label: 'Duplicate', icon: 'duplicate', onSelect: () => alert('Duplicate') },
            { type: 'submenu', label: 'Export as', items: [
                { label: 'CSV', icon: 'fileExport', onSelect: () => alert('CSV') },
                { label: 'PDF', icon: 'reports', onSelect: () => alert('PDF') }
            ] },
            { type: 'separator' },
            { label: 'Delete', icon: 'delete', tone: 'destructive', onSelect: () => alert('Delete') }
        ]}
    >
        <Button appearance="outline" tone="neutral">Open menu</Button>
    </DropdownMenu>
);

const PopoverHero = () => (
    <Popover
        align="start"
        sideOffset={12}
        trigger={<Button appearance="outline" tone="neutral">Open popover</Button>}
        content={
            <div className="tw-flex tw-flex-col tw-gap-1 tw-p-4">
                <Heading as="h4" variant="title-sm">Popover content</Heading>
                <Text tone="muted" variant="body">
                    Short-form contextual content or controls.
                </Text>
            </div>
        }
    />
);

const PopoverMatchTriggerWidthHero = () => {
    const [open, setOpen] = React.useState(false);

    return (
        <Popover
            align="start"
            matchTriggerWidth
            onOpenChange={setOpen}
            open={open}
            preventAutoFocus
            trigger={
                <div className="tw-w-72">
                    <Input
                        onFocus={() => setOpen(true)}
                        placeholder="Search indicators…"
                        type="search"
                    />
                </div>
            }
            content={
                <div className="tw-flex tw-flex-col">
                    <div className="tw-border-b tw-border-border tw-px-3 tw-py-2">
                        <Text tone="muted" variant="caption">3 results</Text>
                    </div>
                    <div className="tw-flex tw-flex-col tw-px-3 tw-py-2 tw-gap-1">
                        <Text variant="body">Site A · Electricity</Text>
                        <Text variant="body">Site A · Water</Text>
                        <Text variant="body">Site B · Electricity</Text>
                    </div>
                </div>
            }
        />
    );
};

const TooltipHero = () => (
    <Tooltip content="Short hint shown on hover or focus" side="bottom">
        <Button appearance="outline" tone="neutral">Tooltip trigger</Button>
    </Tooltip>
);

const overlayStorySections = [
    createFamily({
        id: 'dialog',
        label: 'Dialog',
        description: 'Centred modal. Closed primitive: caller passes `title` / `description` / `actions` as props (header and footer chrome rendered automatically) and the body as `children`. `closable` (default true) is the single switch for casual dismissal — when false the X is hidden and Escape / click-outside become inert.',
        subcomponents: ['Dialog'],
        stories: [{ id: 'dialog-basic', title: 'Working dialog', hero: DialogHero }]
    }),
    createFamily({
        id: 'dropdown-menu',
        label: 'DropdownMenu',
        description: 'Click-anchored menu with structured items. Closed primitive: caller passes `trigger` plus an `items` array. Item types: `item` (default), `separator`, `header`, `submenu` (recursive). Items support an optional trailing `icon`; submenu items reserve the right slot for the chevron. Modal mode is forced off — kebab / action menus shouldn\'t trap focus.',
        subcomponents: ['DropdownMenu'],
        stories: [{ id: 'dropdown-menu-basic', title: 'Button dropdown', hero: DropdownMenuHero }]
    }),
    createFamily({
        id: 'popover',
        label: 'Popover',
        description: 'Click-anchored floating panel. Closed primitive: caller passes `trigger` (the click target) and `content` (the panel) as props — both ReactNode. Positioning, controlled state and Radix focus events live as props on the root.',
        subcomponents: ['Popover'],
        stories: [
            { id: 'popover-basic', title: 'Anchored popover', hero: PopoverHero },
            { id: 'popover-match-trigger-width', title: 'Match trigger width (combobox / typeahead)', hero: PopoverMatchTriggerWidthHero }
        ]
    }),
    createFamily({
        id: 'tooltip',
        label: 'Tooltip',
        description: 'Short hover/focus hint anchored to a single trigger. Closed primitive: caller passes `content` (string) plus the trigger element as `children`. Single positioning axis (`side`); offset, alignment and timing are baked. The shared `<TooltipProvider>` is mounted once in `ScopedUiRoot`.',
        subcomponents: ['Tooltip', 'TooltipProvider'],
        stories: [{ id: 'tooltip-basic', title: 'Tooltip trigger', hero: TooltipHero }]
    })
].map(withStorySources);

export { overlayStorySections };
